import { Component } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MediaLibrariFileForAiDto } from 'app/api/models/mediaLibrary/mediaLibrariFileForAiDto';
import { MediaLibraryFileDto } from 'app/api/models/mediaLibrary/mediaLibraryFileDto';
import { ToastService, MessageText } from 'app/helpers/services/toastService';
import { TenantsService } from 'app/state/tenants.service';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

export interface TenantEmailTemplateDto {
  tenantId: string | null;
  emailTemplate: string;
  footerText: string | null;
  backgroundColor: string | null;
}
@Component({
    selector: 'app-tenant-email-template',
    templateUrl: './tenant-email-template.component.html',
    styleUrl: './tenant-email-template.component.scss',
    standalone: false
})
export class TenantEmailTemplateComponent {
  id: string | null = null;
  tenantName: string | null = null;
  form: FormGroup;
  emailTemplateData: TenantEmailTemplateDto | null = null;
  backgroundColor?: string;
  previewHtml: SafeHtml = '';
  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    public service: TenantsService,
    private sanitizer: DomSanitizer,
    private toastService: ToastService,
  ) {
    this.form = this.fb.group({
      emailTemplate: ['', [Validators.required]],
      footerText: [''],
      bodyText: [''],
      backgroundColor: [
        null,
        [Validators.pattern(/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/)],
      ],
    });
  }

  ngOnInit(): void {
    this.route.paramMap.subscribe((params) => {
      this.id = params.get('id');
      if (this.id) {
        this.service.getEmailTemplate(this.id).subscribe((x) => {
          this.emailTemplateData = x;

          const parser = new DOMParser();
          const doc = parser.parseFromString(x.emailTemplate, 'text/html');

          const footerNode = doc.querySelector('#text_div504');
          const footerContent = footerNode ? footerNode.innerHTML : '';

          this.form.patchValue({
            emailTemplate: x.emailTemplate,
            footerText: footerContent,
            backgroundColor: x.backgroundColor,
          });
          this.previewHtml = this.sanitizer.bypassSecurityTrustHtml(
            x.emailTemplate,
          );
        });
      }

      this.form.valueChanges.subscribe((val) => {
        this.updatePreviewFromEditor(val);
      });
    });
  }

  updatePreviewFromEditor(val: any) {
    if (!val.emailTemplate) return;

    const parser = new DOMParser();
    const doc = parser.parseFromString(val.emailTemplate, 'text/html');

    const footerNode = doc.querySelector('#text_div504');
    if (footerNode) {
      footerNode.innerHTML = val.footerText || '';
    }
    const bodyNode = doc.querySelector('body');
    if (bodyNode && val.backgroundColor) {
      bodyNode.setAttribute('bgcolor', val.backgroundColor);
      bodyNode.style.backgroundColor = val.backgroundColor;
    }
    if (val.backgroundColor) {
      const allNodes = doc.querySelectorAll<HTMLElement>(
        '[style*="background"]',
      );
      allNodes.forEach((el) => {
        el.style.background = val.backgroundColor;
      });
    }
    const updatedHtml = doc.documentElement.outerHTML;

    this.previewHtml = this.sanitizer.bypassSecurityTrustHtml(updatedHtml);

    this.form.patchValue({ emailTemplate: updatedHtml }, { emitEvent: false });
  }

  save(): void {
    if (!this.id) return;

    const emailTemplate: TenantEmailTemplateDto = {
      tenantId: this.id,
      emailTemplate: this.form.value.emailTemplate?.trim() ?? '',
      footerText: this.form.value.footerText ?? null,
      backgroundColor: this.form.value.backgroundColor ?? null,
    };

    if (!emailTemplate) return;
    this.service.updateEmailTemplate(emailTemplate).subscribe(() => {
      this.toastService.showSuccess(MessageText.saved);
    });
  }
}
